/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import LoadingSpinner from '../LoadingSpinner'
import { useWorkflowsService } from '../../hooks/useWorkflowsService'
import WorkflowSteps from '../workflow/WorkflowSteps'
import WorkflowCommentsModal from './WorkflowCommentsModal'
import WorkflowCancellationCommentModal from './WorkflowCancellationCommentModal'
import { ReadCustomJustificationModal } from './ReadCustomJustificationModal'
import type { WorkflowStep, TaskTeamItem } from '../../services/api/workflows'
import TaskDocLink from '../../components/shared/TaskDocLink'
import TextLinkButton from '../shared/TextLinkButton'
import ConfirmRedirectModal from './ConfirmRedirectModal'

interface ModalLevelClasses {
  modalClass?: string
  backDropClass?: string
  backdropClassName?: string
  commentsModalClass?: string
  commentsBackdropClass?: string
}

interface WorkflowDetailsModalProps {
  wfId: number
  onClose?: () => void
  modalLevelClasses?: ModalLevelClasses
  isSuwfFlow?: boolean
  handleRedirect?: () => void
}

interface StepContent {
  noApprovers: boolean
  notRequired?: boolean
  effectiveDate?: number
}

interface ExtendedWorkflowData {
  id: number
  workflowNo?: string
  formattedId?: string
  description?: string
  justificationText?: string
  customJustification?: boolean
  workflowType: string
  initiatorEmail?: string
  initiatorName?: string
  initiatorUserId?: number
  status: string
  startDate?: number
  endDate?: number
  parentFolderId?: number
  workflowSteps?: WorkflowStep[]
  stepsWithContent?: Record<number, StepContent>
  currentPhase?: { readableType: string }
  currentAssignees?: TaskTeamItem[]
  parentFolder?: { id: number; name: string; systemFolderType?: string }
  parentFolderPath?: string
  cancelledByUser?: {
    email?: string
    firstName: string
    lastName: string
  }
  study?: { name: string; id?: number }
  site?: { name: string; id?: number }
  files?: any[]
  filesCount?: number
  filename?: string
  cancellationComment?: string
}

/**
 * Check if a task team item matches the initiator user ID
 */
const isMatchingInitiator = (item: TaskTeamItem, initiatorUserId: number): boolean => {
  return item.userId === initiatorUserId && !!item.userEmail
}

/**
 * Extract initiator email from workflow steps task items
 */
const extractInitiatorEmail = (
  workflowSteps: WorkflowStep[] | undefined,
  initiatorUserId: number
): string | undefined => {
  if (!workflowSteps) return undefined

  for (const step of workflowSteps) {
    if (!step.taskTeamSteps) continue

    for (const teamStep of step.taskTeamSteps) {
      if (!teamStep.taskTeamItems) continue

      const matchingItem = teamStep.taskTeamItems.find((item) => isMatchingInitiator(item, initiatorUserId))
      if (matchingItem) {
        return matchingItem.userEmail
      }
    }
  }

  return undefined
}

/**
 * Enrich workflow steps with insufficientNOOfApprovers flag on each taskTeamStep.
 * This flag is used by WorkflowSteps component to display warnings.
 *
 * A step has insufficient approvers when:
 * - processingType is 'N_OF_ALL'
 * - AND non-cancelled task team items count is less than nofAllValue
 */
const enrichWorkflowStepsWithInsufficientFlag = (workflowSteps: WorkflowStep[]): WorkflowStep[] => {
  return workflowSteps.map((step) => {
    if (!step || !step.taskTeamSteps) {
      return step
    }

    const enrichedTaskTeamSteps = step.taskTeamSteps.map((teamStep) => {
      const taskTeamItems = teamStep.taskTeamItems || []
      const nonCancelledItems = taskTeamItems.filter((item) => item.status !== 'CANCELLED')
      const nofAllValue = teamStep.nofAllValue ?? 0

      // Compute insufficientNOOfApprovers flag
      const insufficientNOOfApprovers =
        teamStep.processingType === 'N_OF_ALL' && nonCancelledItems.length - nofAllValue < 0

      // Compute activeStep flag (if any task is OPEN)
      const activeStep = taskTeamItems.some((item) => item.status === 'OPEN')

      // Compute countOfDone
      const countOfDone = taskTeamItems.filter((item) => item.status === 'APPROVED').length

      return {
        ...teamStep,
        insufficientNOOfApprovers,
        activeStep,
        countOfDone
      }
    })

    return {
      ...step,
      taskTeamSteps: enrichedTaskTeamSteps
    }
  })
}

const buildStepContent = (step: WorkflowStep | undefined): StepContent => {
  if (!step) return { noApprovers: true }

  const result: StepContent = { noApprovers: true }

  // Check if step has approvers in task team
  // Match legacy logic: noApprovers = false when:
  // 1. taskTeamItems.length > 0 (has users), OR
  // 2. insufficientNOOfApprovers === true (N_OF_ALL with less users than required)
  if (step.taskTeamSteps?.length > 0) {
    const hasApproversOrInsufficient = step.taskTeamSteps.some((teamStep) => {
      const hasUsers = teamStep.taskTeamItems && teamStep.taskTeamItems.length > 0
      const hasInsufficientFlag = teamStep.insufficientNOOfApprovers === true
      return hasUsers || hasInsufficientFlag
    })

    if (hasApproversOrInsufficient) {
      result.noApprovers = false
    }
  }

  // Special handling for Read & Acknowledge step (index 4)
  if (step.readableType === 'Read & Acknowledge' || step.type === 'TRAINING') {
    const acknowledgeStepRequired = (step as unknown as { acknowledgeStepRequired?: boolean }).acknowledgeStepRequired
    if (typeof acknowledgeStepRequired !== 'undefined' && !acknowledgeStepRequired) {
      result.notRequired = true
    }
    const effectiveDate = (step as unknown as { effectiveDate?: number }).effectiveDate
    if (step.taskTeamSteps?.[0] && effectiveDate) {
      result.effectiveDate = effectiveDate
    }
  }

  return result
}

const buildStepsWithContent = (workflowSteps: WorkflowStep[]): Record<number, StepContent> => {
  return {
    0: buildStepContent(workflowSteps[0]),
    1: buildStepContent(workflowSteps[1]),
    2: buildStepContent(workflowSteps[2]),
    3: buildStepContent(workflowSteps[3]),
    4: buildStepContent(workflowSteps[4])
  }
}

const determineCurrentPhase = (
  workflow: ExtendedWorkflowData,
  t: (key: string) => string
): { readableType: string } => {
  const activeStep = workflow.workflowSteps?.find((step) => step?.status === 'ACTIVE')

  if (activeStep) {
    return { readableType: activeStep.readableType ?? '' }
  }

  if (workflow.status === 'FINISHED') {
    return { readableType: t('WORKFLOWS_STEP_STATUS_FINISHED') }
  }

  if (workflow.status === 'CANCELLED') {
    return { readableType: t('WORKFLOWS_STEP_STATUS_CANCELLED') }
  }

  if (workflow.status === 'SUSPENDED') {
    return { readableType: t('WORKFLOWS_STEP_STATUS_SUSPENDED') }
  }

  return { readableType: t('WORKFLOWS_STEP_STATUS_COMPLETED') }
}

const extractOpenTasks = (workflowSteps: WorkflowStep[] | undefined): TaskTeamItem[] => {
  const allOpenTasks: TaskTeamItem[] = []

  if (!workflowSteps) return allOpenTasks

  const collectOpenTask = (task: TaskTeamItem) => {
    if (task?.status === 'OPEN') {
      allOpenTasks.push(task)
    }
  }

  workflowSteps.forEach((step) => {
    step?.taskTeamSteps?.forEach((teamStep) => {
      teamStep?.taskTeamItems?.forEach(collectOpenTask)
    })
  })

  return allOpenTasks
}

interface ParentFolderDetails {
  parentFolder: { id: number; name: string } | null
  parentFolderPath: string | null
}

const loadParentFolderDetails = async (
  parentFolderId: number,
  api: {
    files: {
      getDetails: (id: number, p1: null, p2: null, p3: boolean) => Promise<{ id: number; name: string }>
      getFsiPath: (id: number, p: boolean) => Promise<Array<{ name: string }>>
    }
  }
): Promise<ParentFolderDetails> => {
  try {
    const [fileDetails, filePathArray] = await Promise.all([
      api.files.getDetails(parentFolderId, null, null, true),
      api.files.getFsiPath(parentFolderId, false)
    ])

    const parentFolderPath =
      filePathArray && Array.isArray(filePathArray) ? filePathArray.map((item) => item.name).join('/') : null

    return {
      parentFolder: fileDetails,
      parentFolderPath
    }
  } catch (error) {
    console.error('Error loading parent folder details:', error)
    return {
      parentFolder: null,
      parentFolderPath: null
    }
  }
}

/**
 * Workflow Details Modal Component
 *
 * Displays comprehensive workflow information including:
 * - Main workflow details (number, description, type, initiator, etc.)
 * - Workflow steps with progress visualization
 * - Current phase and status information
 * - EOS/ETMF specific information when applicable
 *
 * This modal is opened from four possible locations in the Workflows table:
 * 1. Clicking the workflow number/ID
 * 2. Clicking the workflow type
 * 3. Clicking the progress bar
 * 4. Selecting "Details" from the actions menu
 */
export const WorkflowDetailsModal: React.FC<WorkflowDetailsModalProps> = ({
  wfId,
  onClose,
  modalLevelClasses,
  isSuwfFlow,
  handleRedirect
}) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { api } = context
  const navigate = useNavigate()
  const { hideModal, showModal } = useModal()
  const { getReadableType } = useWorkflowsService()
  const modalRef = useRef<HTMLDivElement>(null)
  const [workflow, setWorkflow] = useState<ExtendedWorkflowData | null>(null)
  const [loading, setLoading] = useState(true)

  // Drag state
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 })
  const dragStartPos = useRef({ x: 0, y: 0 })

  useEffect(() => {
    // Add modal-open class to body
    document.body.classList.add('modal-open')

    const loadWorkflow = async () => {
      try {
        setLoading(true)

        const data = await api.workflows.getWorkflow(wfId)

        const extendedData: ExtendedWorkflowData = {
          ...data,
          // Preserve full file structure for TaskDocLink compatibility
          files: data.files,
          filesCount: data.files?.length || 0
        }

        if (!extendedData.initiatorEmail && extendedData.initiatorUserId) {
          extendedData.initiatorEmail = extractInitiatorEmail(extendedData.workflowSteps, extendedData.initiatorUserId)
        }

        // Enrich workflow steps with insufficientNOOfApprovers flag
        if (extendedData.workflowSteps) {
          extendedData.workflowSteps = enrichWorkflowStepsWithInsufficientFlag(extendedData.workflowSteps)
        }

        if (extendedData.workflowSteps && !extendedData.stepsWithContent) {
          extendedData.stepsWithContent = buildStepsWithContent(extendedData.workflowSteps)
        }

        if (!extendedData.currentPhase) {
          extendedData.currentPhase = determineCurrentPhase(extendedData, t)
        }

        if (!extendedData.currentAssignees) {
          extendedData.currentAssignees = extractOpenTasks(extendedData.workflowSteps)
        }

        if (extendedData.parentFolderId) {
          const folderDetails = await loadParentFolderDetails(extendedData.parentFolderId, api)
          extendedData.parentFolder = folderDetails.parentFolder ?? undefined
          extendedData.parentFolderPath = folderDetails.parentFolderPath ?? undefined
        }

        setWorkflow(extendedData)
        setLoading(false)
      } catch (error) {
        console.error('Error loading workflow:', error)
        setLoading(false)
      }
    }

    loadWorkflow()

    return () => {
      // Clean up: remove modal-open class when modal unmounts
      document.body.classList.remove('modal-open')
    }
  }, [wfId, api, t])

  const handleClose = () => {
    if (onClose) {
      onClose()
      return
    }
    hideModal()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  /**
   * Handle mouse down on modal header to start dragging
   */
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging from modal header (not buttons)
    if ((e.target as HTMLElement).closest('.close, button')) {
      return
    }

    setIsDragging(true)
    dragStartPos.current = { x: e.clientX, y: e.clientY }
    setDragOffset({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    })
  }

  /**
   * Handle mouse move during drag
   */
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return

    const newX = e.clientX - dragOffset.x
    const newY = e.clientY - dragOffset.y

    setModalPosition({ x: newX, y: newY })
  }

  /**
   * Handle mouse up to stop dragging
   */
  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Add and remove mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragOffset])

  /**
   * Format date/time according to user preferences
   */
  const formatDateTime = (timestamp: number | null | undefined): string => {
    if (!timestamp) return t('NOT_AVAILABLE')
    // Use simple date formatting for now
    // TODO: Implement full date/time formatting with user preferences
    const date = new Date(timestamp)
    return date.toLocaleString()
  }

  /**
   * Render workflow main information section
   */
  const renderMainInfo = () => {
    if (!workflow) return null

    const isEosWf = workflow.workflowType === 'EOS_DOWNLOAD_AND_ACKNOWLEDGE'
    const isQcReview = workflow.workflowType === 'QC_REVIEW'

    return (
      <div className="main-info">
        <div>
          <div className="clearfix">
            <strong className="title">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</strong>
            <p className="info">{workflow.workflowNo ?? workflow.formattedId}</p>
          </div>

          <div className="clearfix">
            <strong className="title">{t('DESCRIPTION')}</strong>
            <p className="info word-break">{workflow.description ?? t('NOT_AVAILABLE')}</p>
          </div>

          {workflow.justificationText && (
            <div className="clearfix">
              <strong className="title">{t('JUSTIFICATION')}</strong>
              <p className="info">
                {!workflow.customJustification ? (
                  workflow.justificationText
                ) : (
                  <TextLinkButton
                    action={() => {
                      showModal(
                        <ReadCustomJustificationModal
                          workflow={{
                            id: workflow.id,
                            workflowType: workflow.workflowType,
                            justificationText: workflow.justificationText
                          }}
                        />,
                        { keepParentModalsOpen: true }
                      )
                    }}
                    title={workflow.justificationText}
                  >
                    {workflow.justificationText.length > 44
                      ? workflow.justificationText.substring(0, 44) + '...'
                      : workflow.justificationText}
                  </TextLinkButton>
                )}
              </p>
            </div>
          )}

          <div className="clearfix">
            <strong className="title">{t('TYPE')}</strong>
            <p className="info">{getReadableType(workflow.workflowType)}</p>
          </div>

          {isEosWf && workflow.study ? (
            <div className="clearfix">
              <strong className="title">{t('EMTF_MD_STUDY')}</strong>
              <p className="info">
                {!workflow.study?.id ? (
                  t('NOT_AVAILABLE')
                ) : (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      const navigateToStudy = () => {
                        handleClose()
                        const folderType = workflow.parentFolder?.systemFolderType || ''
                        navigate(`/browser/${workflow.study!.id}${folderType ? `?type=${folderType}` : ''}`)
                        if (handleRedirect) {
                          handleRedirect()
                        }
                      }
                      if (isSuwfFlow) {
                        showModal(
                          <ConfirmRedirectModal
                            updateConfirmation={(confirmed) => {
                              if (confirmed) {
                                navigateToStudy()
                              }
                            }}
                            modalClasses={modalLevelClasses}
                          />,
                          { keepParentModalsOpen: true }
                        )
                      } else {
                        navigateToStudy()
                      }
                    }}
                    title={workflow.study.name}
                    className="study-folder-redirect-link"
                  >
                    {workflow.study.name}
                  </a>
                )}
              </p>
            </div>
          ) : (
            <div className="clearfix">
              <strong className="title">{t('DOCUMENT(S)')}</strong>
              <TaskDocLink
                task={workflow as any}
                isWorkflow={true}
                hideIcon={true}
                noPadding={true}
                keepParentModalsOpen={true}
                onNavigate={handleClose}
              />
            </div>
          )}

          <div className="clearfix">
            <strong className="title">{t('START_DATE')}</strong>
            <p className="info">{formatDateTime(workflow.startDate)}</p>
          </div>
        </div>

        <div>
          <div className="clearfix">
            <strong className="title">{t('INITIATOR')}</strong>
            <p className="info">
              {workflow.initiatorEmail ? (
                <a href={`mailto:${workflow.initiatorEmail}`}>{workflow.initiatorName}</a>
              ) : (
                <span>
                  {workflow.initiatorName} {t('INITIATOR_INACTIVE')}
                </span>
              )}
            </p>
          </div>

          <div className="clearfix">
            <strong className="title">{t('CURRENT_PHASE')}</strong>
            <p className="info">{workflow.currentPhase?.readableType ?? t('NOT_AVAILABLE')}</p>
          </div>

          {workflow.cancelledByUser && (
            <div className="clearfix">
              <strong className="title">{t('WORKFLOW_DETAIL_CANCELLED_BY')}</strong>
              <p className="info">
                {workflow.cancelledByUser.email ? (
                  <a href={`mailto:${workflow.cancelledByUser.email}`}>
                    {workflow.cancelledByUser.firstName} {workflow.cancelledByUser.lastName}
                  </a>
                ) : (
                  <span>
                    {workflow.cancelledByUser.firstName} {workflow.cancelledByUser.lastName}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Current Assignee(s) - only show if not cancelled */}
          {workflow.status !== 'CANCELLED' && (
            <div className="clearfix">
              <strong className="title">{t('CURRENT_ASSIGNEE(S)')}</strong>
              <p className="info">
                {workflow.currentAssignees && workflow.currentAssignees.length > 1 && (
                  <span>{t('MULTIPLE_USERS')}</span>
                )}
                {workflow.currentAssignees && workflow.currentAssignees.length === 0 && (
                  <span>{t('NOT_AVAILABLE')}</span>
                )}
                {workflow.currentAssignees &&
                  workflow.currentAssignees.length === 1 &&
                  workflow.currentAssignees.map((assignee) => (
                    <span key={assignee?.userId ?? assignee?.userEmail}>
                      <a href={`mailto:${assignee.userEmail}`}>{assignee.userName}</a>
                    </span>
                  ))}
              </p>
            </div>
          )}

          {/* Canceled On - only show if cancelled */}
          {workflow.status === 'CANCELLED' && workflow.endDate && (
            <div className="clearfix">
              <strong className="title">{t('CANCELED_ON')}</strong>
              <p className="info">{formatDateTime(workflow.endDate)}</p>
            </div>
          )}

          {/* EOS Site or Parent Folder */}
          {isEosWf ? (
            <div className="clearfix">
              <strong className="title">{t('EMTF_MD_SITE')}</strong>
              <p className="info">
                {!workflow.site?.id ? (
                  t('NOT_AVAILABLE')
                ) : (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (workflow.site?.id) {
                        const navigateToSite = () => {
                          handleClose()
                          const folderType = workflow.parentFolder?.systemFolderType || ''
                          navigate(`/browser/${workflow.site!.id}${folderType ? `?type=${folderType}` : ''}`)
                          if (handleRedirect) {
                            handleRedirect()
                          }
                        }
                        if (isSuwfFlow) {
                          showModal(
                            <ConfirmRedirectModal
                              updateConfirmation={(confirmed) => {
                                if (confirmed) {
                                  navigateToSite()
                                }
                              }}
                              modalClasses={modalLevelClasses}
                            />,
                            { keepParentModalsOpen: true }
                          )
                        } else {
                          navigateToSite()
                        }
                      }
                    }}
                    title={workflow.site.name}
                    className="study-folder-redirect-link"
                  >
                    {workflow.site.name}
                  </a>
                )}
              </p>
            </div>
          ) : (
            !isQcReview && (
              <div className="clearfix">
                <strong className="title">{t('PARENT_FOLDER')}</strong>
                <p className="info">
                  {!workflow.parentFolder?.id ? (
                    workflow.parentFolderPath ? (
                      <span>{workflow.parentFolderPath.split('/').pop()}</span>
                    ) : (
                      t('NOT_AVAILABLE')
                    )
                  ) : (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (workflow.parentFolder?.id) {
                          // Close modal before navigating
                          handleClose()
                          // Use hash-based navigation for legacy AngularJS routing compatibility
                          const folderType = workflow.parentFolder.systemFolderType || ''
                          navigate(`/browser/${workflow.parentFolder.id}${folderType ? `?type=${folderType}` : ''}`)
                        }
                      }}
                      title={workflow.parentFolderPath}
                    >
                      {workflow.parentFolder.name}
                    </a>
                  )}
                </p>
              </div>
            )
          )}

          {/* Cancellation Comment - only if cancelled */}
          {workflow.status === 'CANCELLED' && workflow.cancellationComment && (
            <div className="clearfix">
              <strong className="title">{t('CANCELLATION_COMMENT')}</strong>
              <p className="info">
                <button
                  className="btn btn-link"
                  onClick={() =>
                    showModal(<WorkflowCancellationCommentModal workflow={workflow} />, {
                      keepParentModalsOpen: true
                    })
                  }
                >
                  {t('VIEW_CANCELLATION_COMMENT')}
                </button>
              </p>
            </div>
          )}

          {/* Comments - only if not finished or cancelled */}
          {!['FINISHED', 'CANCELLED'].includes(workflow.status) && (
            <div className="clearfix view-comments-link">
              <strong className="title">{t('COMMENTS')}</strong>
              <p className="info">
                <button
                  className="btn btn-link"
                  onClick={() =>
                    showModal(<WorkflowCommentsModal workflow={workflow} isSuwfFlow={isSuwfFlow} />, {
                      keepParentModalsOpen: true
                    })
                  }
                >
                  {t('VIEW_COMMENTS')}
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="theme-sandman">
      {/* Modal Dialog - backdrop handled via inline style for proper stacking */}
      <div
        ref={modalRef}
        className={`modal fade in workflow-detail ${modalLevelClasses?.modalClass ?? ''}`}
        style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        tabIndex={-1}
        role="dialog"
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
      >
        <div
          className="modal-dialog modal-lg"
          onClick={(e) => e.stopPropagation()}
          style={{
            transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
        >
          <div className="modal-content">
            <div
              className="modal-header"
              onMouseDown={handleMouseDown}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              <button
                type="button"
                className="close"
                onClick={handleClose}
                aria-label="Close"
                style={{
                  fontSize: '32px',
                  lineHeight: '1',
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  padding: '0',
                  opacity: '0.8'
                }}
              >
                <span aria-hidden="true">&times;</span>
              </button>
              <h4 className="modal-title" id="ModalHeader">
                {t('WORKFLOW_DETAIL_TITLE')}
              </h4>
            </div>

            <div className="modal-body">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <LoadingSpinner size={40} color="#3374c8" />
                </div>
              ) : (
                <>
                  {renderMainInfo()}
                  {workflow && <WorkflowSteps workflow={workflow} />}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                data-action="ok"
                className="btn btn-primary text-capitalize"
                onClick={handleClose}
                id="8ff99bb0-a062-437d-b2b5-6f44e5ba1490"
              >
                {t('OK')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WorkflowDetailsModal
